import axios from "axios";
import { useState, useContext, useEffect, useCallback } from "react";
import { AuthContext } from "../../context/AuthContext";
import {
    Card,
    Tabs,
    Table,
    Input,
    Button,
    DatePicker,
    Tag,
    Space,
    Modal,
    Popconfirm,
    message,
    Typography,
    Divider,
    Empty,
} from "antd";
import {
    SearchOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    ReloadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";

const { Title } = Typography;
const { RangePicker } = DatePicker;
const { TextArea } = Input;

const TABS = [
    { key: "Pending Approval", label: "Pending Approval" },
    { key: "Available", label: "Approved" },
    { key: "Rejected", label: "Rejected" },
];

const STATUS_COLORS = {
    "Pending Approval": "orange",
    Available: "green",
    Rejected: "red",
};

export default function CompoffApproval() {
    const { authData, backUrl } = useContext(AuthContext);

    // ✅ NEW: pick up emp_id / date range from the page's own URL, if present
    // e.g. deep-linked from an employee's profile: ?employee=123&from_date=2026-01-01&to_date=2026-01-31
    const urlParams = new URLSearchParams(window.location.search);
    const empIdFromUrl = urlParams.get("employee") || urlParams.get("emp_id");
    const fromDateFromUrl = urlParams.get("from_date");
    const toDateFromUrl = urlParams.get("to_date");

    const [activeTab, setActiveTab] = useState("Pending Approval");
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [dateRange, setDateRange] = useState(
        fromDateFromUrl && toDateFromUrl
            ? [dayjs(fromDateFromUrl), dayjs(toDateFromUrl)]
            : null
    );

    const [actionLoadingId, setActionLoadingId] = useState(null);

    const [rejectModalOpen, setRejectModalOpen] = useState(false);
    const [rejectTarget, setRejectTarget] = useState(null);
    const [rejectReason, setRejectReason] = useState("");
    const [rejectSubmitting, setRejectSubmitting] = useState(false);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const params = { status: activeTab };

            if (search.trim()) {
                params.emp_name = search.trim();
            }

            // ✅ NEW: forward emp_id filter to the backend, if given
            if (empIdFromUrl) {
                params.employee = empIdFromUrl;
            }

            if (dateRange && dateRange.length === 2) {
                params.from_date = dayjs(dateRange[0]).format("YYYY-MM-DD");
                params.to_date = dayjs(dateRange[1]).format("YYYY-MM-DD");
            }

            const res = await axios.get(`${backUrl}/api/hr/compoffs/`, {
                headers: { Authorization: `Bearer ${authData?.access}` },
                params,
            });

            const results = res.data?.results ?? res.data ?? [];
            setData(results);
        } catch (err) {
            message.error("Failed to load comp-off requests.");
            setData([]);
        } finally {
            setLoading(false);
        }
    }, [activeTab, backUrl, authData, empIdFromUrl, search, dateRange]);

    useEffect(() => {
        fetchData();
    }, [activeTab]);

    const handleSearch = () => fetchData();

    const handleResetFilters = () => {
        setSearch("");
        setDateRange(null);
        setTimeout(fetchData, 0);
    };

    const handleApprove = async (record) => {
        setActionLoadingId(record.id);
        try {
            await axios.put(
                `${backUrl}/api/hr/compoffs/${record.id}/approve/`,
                {},
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            message.success(`Comp-off for ${record.employee_name} approved.`);
            fetchData();
        } catch (err) {
            const errMsg =
                err.response?.data?.error || err.response?.data?.message || "Failed to approve comp-off.";
            message.error(errMsg);
        } finally {
            setActionLoadingId(null);
        }
    };

    const openRejectModal = (record) => {
        setRejectTarget(record);
        setRejectReason("");
        setRejectModalOpen(true);
    };

    const handleRejectSubmit = async () => {
        if (!rejectReason.trim()) {
            message.warning("Please enter a rejection reason.");
            return;
        }
        setRejectSubmitting(true);
        try {
            await axios.put(
                `${backUrl}/api/hr/compoffs/${rejectTarget.id}/reject/`,
                { cancellation_reason: rejectReason.trim() },
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            message.success(`Comp-off for ${rejectTarget.employee_name} rejected.`);
            setRejectModalOpen(false);
            setRejectTarget(null);
            fetchData();
        } catch (err) {
            const errMsg =
                err.response?.data?.error ||
                err.response?.data?.message ||
                "Failed to reject comp-off.";
            message.error(errMsg);
        } finally {
            setRejectSubmitting(false);
        }
    };

    const columns = [
        {
            title: "Emp Code",
            dataIndex: ["employee", "emp_code"],
            key: "emp_code",
            width: 110,
        },
        {
            title: "Employee Name",
            dataIndex: ["employee", "full_name"],
            key: "employee_name",
        },
        {
            title: "Department",
            dataIndex: ["employee", "department_name"],
            key: "department_name",
            render: (val) => val || "-",
        },
        {
            title: "Work Date",
            dataIndex: "work_date",
            key: "work_date",
            render: (val) => (val ? dayjs(val).format("DD/MM/YYYY") : "-"),
        },
        {
            title: "Reason",
            dataIndex: "reason",
            key: "reason",
            ellipsis: true,
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "current_status",
            width: 140,
            render: (val) => <Tag color={STATUS_COLORS[val] || "default"}>{val}</Tag>,
        },
        ...(activeTab === "Available"
            ? [
                {
                    title: "Expiry Date",
                    dataIndex: "expiry_date",
                    key: "expiry_date",
                    render: (val) => (val ? dayjs(val).format("DD/MM/YYYY") : "-"),
                },
            ]
            : []),
        ...(activeTab === "Rejected"
            ? [
                {
                    title: "Rejection Reason",
                    dataIndex: "cancellation_reason",
                    key: "cancellation_reason",
                    ellipsis: true,
                },
            ]
            : []),
        ...(activeTab === "Pending Approval"
            ? [
                {
                    title: "Actions",
                    key: "actions",
                    width: 220,
                    render: (_, record) => (
                        <Space>
                            <Popconfirm
                                title="Approve this comp-off request?"
                                onConfirm={() => handleApprove(record)}
                                okText="Yes, Approve"
                                cancelText="Cancel"
                            >
                                <Button
                                    type="primary"
                                    size="small"
                                    icon={<CheckCircleOutlined />}
                                    loading={actionLoadingId === record.id}
                                    style={{ backgroundColor: "#0e497b", borderColor: "#0e497b" }}
                                >
                                    Approve
                                </Button>
                            </Popconfirm>
                            <Button
                                danger
                                size="small"
                                icon={<CloseCircleOutlined />}
                                onClick={() => openRejectModal(record)}
                                loading={actionLoadingId === record.id}
                            >
                                Reject
                            </Button>
                        </Space>
                    ),
                },
            ]
            : []),
    ];

    return (
        <div style={{ padding: "24px" }}>
            <>
                <Title level={4} style={{ textAlign: "center", color: "#0e497b" }}>
                    Comp-Off Approval
                </Title>

                <Divider />

                <Tabs
                    activeKey={activeTab}
                    onChange={(key) => setActiveTab(key)}
                    items={TABS.map((t) => ({ key: t.key, label: t.label }))}
                />

                <Space
                    wrap
                    style={{ marginBottom: 16, width: "100%", justifyContent: "space-between" }}
                >
                    <Space wrap>
                        <Input
                            placeholder="Search by emp code / name"
                            prefix={<SearchOutlined />}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onPressEnter={handleSearch}
                            style={{ width: 240 }}
                            allowClear
                        />
                        <RangePicker
                            value={dateRange}
                            onChange={(vals) => setDateRange(vals)}
                            format="DD/MM/YYYY"
                        />
                        <Button
                            type="primary"
                            onClick={handleSearch}
                            icon={<SearchOutlined />}
                            style={{ backgroundColor: "#0e497b", borderColor: "#0e497b" }}
                        >
                            Filter
                        </Button>
                        <Button onClick={handleResetFilters} icon={<ReloadOutlined />}>
                            Reset
                        </Button>
                    </Space>
                </Space>

                <Table
                    rowKey="id"
                    columns={columns}
                    dataSource={data}
                    loading={loading}
                    pagination={{ pageSize: 10, showSizeChanger: true }}
                    scroll={{ x: "max-content" }}
                    locale={{
                        emptyText: <Empty description={`No ${activeTab} requests found.`} />,
                    }}
                />
            </>

            <Modal
                title={`Reject Comp-Off Request${rejectTarget ? ` — ${rejectTarget.employee_name}` : ""
                    }`}
                open={rejectModalOpen}
                onCancel={() => setRejectModalOpen(false)}
                onOk={handleRejectSubmit}
                confirmLoading={rejectSubmitting}
                okText="Reject"
                okButtonProps={{ danger: true }}
            >
                <p>Please provide a reason for rejecting this comp-off request:</p>
                <TextArea
                    rows={4}
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="Enter rejection reason..."
                />
            </Modal>
        </div>
    );
}